"use client";

import { useEffect, useState, useRef } from "react";
import { ArrowUpRight, Menu, ChevronDown } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import type { MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [activeSubDropdown, setActiveSubDropdown] = useState<string | null>(null);
  const dropdownCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const subDropdownCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openDropdown = (id: string) => {
    if (dropdownCloseTimer.current) clearTimeout(dropdownCloseTimer.current);
    setActiveDropdown(id);
  };
  const scheduleCloseDropdown = () => {
    if (dropdownCloseTimer.current) clearTimeout(dropdownCloseTimer.current);
    dropdownCloseTimer.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 200);
  };
  const openSubDropdown = (id: string) => {
    if (subDropdownCloseTimer.current) clearTimeout(subDropdownCloseTimer.current);
    setActiveSubDropdown(id);
  };
  const scheduleCloseSubDropdown = () => {
    if (subDropdownCloseTimer.current) clearTimeout(subDropdownCloseTimer.current);
    subDropdownCloseTimer.current = setTimeout(() => {
      setActiveSubDropdown(null);
    }, 200);
  };
  useEffect(() => {
    return () => {
      if (dropdownCloseTimer.current) clearTimeout(dropdownCloseTimer.current);
      if (subDropdownCloseTimer.current) clearTimeout(subDropdownCloseTimer.current);
    };
  }, []);
  if (loading) {
    return <div className="flex items-center gap-6">
        <div className="h-4 w-14 bg-[hsla(210_30%_96%/0.08)] animate-pulse rounded-md" />
        <div className="h-4 w-14 bg-[hsla(210_30%_96%/0.08)] animate-pulse rounded-md" />
        <div className="h-4 w-14 bg-[hsla(210_30%_96%/0.08)] animate-pulse rounded-md" />
        <div className="h-4 w-14 bg-[hsla(210_30%_96%/0.08)] animate-pulse rounded-md" />
      </div>;
  }
  const renderNestedItems = (items: MenuItem[], parentId: string) => {
    return <motion.ul initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 8
    }} transition={{
      duration: 0.2
    }} className="absolute top-0 left-full ml-1 w-56 rounded-lg bg-popover text-popover-foreground border shadow-lg py-2 z-50 flex flex-col gap-1 pointer-events-auto" onMouseEnter={() => {
      if (subDropdownCloseTimer.current) clearTimeout(subDropdownCloseTimer.current);
    }} onMouseLeave={scheduleCloseSubDropdown}>
        {items.map((subItem, subIndex) => {
        const hasGrandChildren = subItem.children && subItem.children.length > 0;
        return <li key={subItem.id} data-index={subIndex} className="relative px-2" onMouseEnter={() => hasGrandChildren && openSubDropdown(subItem.id)} onMouseLeave={() => hasGrandChildren && scheduleCloseSubDropdown()}>
              <div className="flex items-center justify-between w-full">
                <Link to={subItem.href} className="block w-full px-2 py-1.5 text-sm text-foreground hover:bg-accent hover:text-accent-foreground rounded-md transition-colors duration-200">
                  {subItem.label}
                </Link>
                {hasGrandChildren && <ChevronDown className="w-3.5 h-3.5 text-muted-foreground -rotate-90 mr-1" />}
              </div>
              {hasGrandChildren && activeSubDropdown === subItem.id && renderNestedItems(subItem.children!, subItem.id)}
            </li>;
      })}
      </motion.ul>;
  };
  return <ul className="flex items-center gap-7">
      {menuItems.map((item, i) => {
      const hasChildren = item.children && item.children.length > 0;
      return <li key={item.id} data-index={i} className="relative py-2" onMouseEnter={() => hasChildren && openDropdown(item.id)} onMouseLeave={() => {
        if (hasChildren) {
          scheduleCloseDropdown();
          scheduleCloseSubDropdown();
        }
      }}>
            <div className="flex items-center gap-1">
              <Link to={item.href} className="text-foreground text-[14px] font-normal tracking-[0.01em] hover:text-accent-foreground transition-colors duration-200">
                {item.label}
              </Link>
              {hasChildren && <ChevronDown className="w-3.5 h-3.5 text-muted-foreground transition-transform duration-200" />}
            </div>

            {hasChildren && activeDropdown === item.id && <motion.ul initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 8
        }} transition={{
          duration: 0.2
        }} className="absolute top-full left-0 w-56 rounded-lg bg-popover text-popover-foreground border shadow-lg py-2 z-50 flex flex-col gap-1 pointer-events-auto" onMouseEnter={() => {
          if (dropdownCloseTimer.current) clearTimeout(dropdownCloseTimer.current);
        }} onMouseLeave={scheduleCloseDropdown}>
                {item.children!.map((child, childIndex) => {
            const hasGrandChildren = child.children && child.children.length > 0;
            return <li key={child.id} data-index={childIndex} className="relative px-2" onMouseEnter={() => hasGrandChildren && openSubDropdown(child.id)} onMouseLeave={() => hasGrandChildren && scheduleCloseSubDropdown()}>
                      <div className="flex items-center justify-between w-full">
                        <Link to={child.href} className="block w-full px-2 py-1.5 text-sm text-foreground hover:bg-accent hover:text-accent-foreground rounded-md transition-colors duration-200">
                          {child.label}
                        </Link>
                        {hasGrandChildren && <ChevronDown className="w-3.5 h-3.5 text-muted-foreground -rotate-90 mr-1" />}
                      </div>
                      {hasGrandChildren && activeSubDropdown === child.id && renderNestedItems(child.children!, child.id)}
                    </li>;
          })}
              </motion.ul>}
          </li>;
    })}
    </ul>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const toggleExpand = (id: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  if (loading) {
    return <div className="flex flex-col gap-4 mt-8">
        <div className="h-6 w-32 bg-[hsla(210_30%_96%/0.08)] animate-pulse rounded-md" />
        <div className="h-6 w-32 bg-[hsla(210_30%_96%/0.08)] animate-pulse rounded-md" />
        <div className="h-6 w-32 bg-[hsla(210_30%_96%/0.08)] animate-pulse rounded-md" />
      </div>;
  }
  const renderMobileNested = (items: MenuItem[], level: number = 1) => {
    return <div className={`flex flex-col pl-4 pb-2 gap-2 ${level > 1 ? 'border-l border-border/50 ml-2 mt-1' : ''}`}>
        {items.map((subItem, subIndex) => {
        const hasGrandChildren = subItem.children && subItem.children.length > 0;
        const isExpanded = expandedItems[subItem.id];
        return <div key={subItem.id} data-index={subIndex} className="flex flex-col">
              <div className="flex items-center justify-between py-1.5">
                <Link to={subItem.href} onClick={onNavigate} className={`text-muted-foreground hover:text-accent-foreground transition-colors duration-200 ${level === 1 ? 'text-lg font-normal' : 'text-base font-normal'}`}>
                  {subItem.label}
                </Link>
                {hasGrandChildren && <Button variant="ghost" size="icon" onClick={() => toggleExpand(subItem.id)} className="h-8 w-8 text-foreground hover:bg-accent" aria-label="Expand submenu">
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`} />
                  </Button>}
              </div>
              {hasGrandChildren && isExpanded && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.2
          }} className="overflow-hidden">
                  {renderMobileNested(subItem.children!, level + 1)}
                </motion.div>}
            </div>;
      })}
      </div>;
  };
  return <nav className="flex flex-col gap-1 mt-8 px-6">
      {menuItems.map((item, i) => {
      const hasChildren = item.children && item.children.length > 0;
      const isExpanded = expandedItems[item.id];
      return <div key={item.id} data-index={i} className="border-b border-border">
            <div className="flex items-center justify-between py-4">
              <Link to={item.href} onClick={onNavigate} className="text-foreground text-2xl font-normal tracking-[-0.015em] hover:text-accent-foreground transition-colors duration-240">
                {item.label}
              </Link>
              {hasChildren && <Button variant="ghost" size="icon" onClick={() => toggleExpand(item.id)} className="h-10 w-10 text-foreground hover:bg-accent" aria-label="Expand submenu">
                  <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`} />
                </Button>}
            </div>

            {hasChildren && isExpanded && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.2
        }} className="overflow-hidden pb-4">
                {renderMobileNested(item.children!, 1)}
              </motion.div>}
          </div>;
    })}
    </nav>;
}
export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="31344" id="cabeçalho-principal" className="fixed top-0 inset-x-0 z-50 bg-transparent pointer-events-none">
      <div className="w-full max-w-[1360px] mx-auto px-5 sm:px-8 lg:px-14 pt-5">
        <div className="flex items-center justify-between gap-4 pointer-events-auto">
          <div className="relative flex items-center shrink-0 h-16 px-4">
            <div aria-hidden="true" className="absolute inset-0 -inset-x-6 -inset-y-3 pointer-events-none" style={{
            background: "radial-gradient(ellipse at center, hsla(222,22%,4%,0.4) 0%, hsla(222,22%,4%,0) 70%)"
          }} />
            <Link to="/" className="relative flex items-center">
              <WvcLogo className="h-5 w-auto" style={{
              filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
              color: "#ffffff"
            }} />
            </Link>
          </div>

          <div className="hidden flex-1 justify-center !bg-transparent lg:flex">
            <div className="flex items-center rounded-lg px-6 py-2.5 border !opacity-[0.63] !bg-[rgba(249,250,251,0.8)]" style={{
            borderColor: "hsl(210 10% 85% / 0.5)",
            backdropFilter: "blur(20px) saturate(140%)",
            WebkitBackdropFilter: "blur(20px) saturate(140%)"
          }}>
              <MenuProvider menu_id="16">
                <DesktopMenuConsumer />
              </MenuProvider>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <motion.div className="hidden lg:block" initial={false} whileHover={reduceMotion ? undefined : "hover"}>
              <Link to="/ContactsPage" className="inline-block">
                <div className="flex items-center gap-3 bg-primary text-primary-foreground rounded-lg pl-6 pr-2 py-2 hover:bg-primary/90 transition-colors duration-380">
                  <span className="text-[14px] font-medium tracking-[0.01em]">
                    Consulte-nos
                  </span>
                  <motion.span variants={{
                  hover: reduceMotion ? {} : {
                    rotate: -45,
                    x: 4
                  }
                }} transition={{
                  duration: 0.38,
                  ease: [0.22, 1, 0.36, 1]
                }} className="flex items-center justify-center rounded-lg" style={{
                  width: 28,
                  height: 28,
                  background: "hsl(210 20% 98%)"
                }}>
                    <ArrowUpRight className="w-3.5 h-3.5" style={{
                    color: "hsl(215 90% 45%)"
                  }} />
                  </motion.span>
                </div>
              </Link>
            </motion.div>

            <div className="lg:hidden">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Abrir menu" className="rounded-lg text-foreground hover:bg-transparent border h-12 w-12 bg-accent/50" style={{
                  borderColor: "hsl(210 10% 85% / 0.5)",
                  backdropFilter: "blur(20px) saturate(140%)",
                  WebkitBackdropFilter: "blur(20px) saturate(140%)"
                }}>
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="bg-background text-foreground border-l w-full sm:max-w-md p-0 overflow-y-auto" style={{
                borderColor: "hsl(210 10% 85% / 0.5)"
              }}>
                  <SheetHeader className="px-6 pt-6">
                    <SheetTitle className="text-foreground text-[11px] tracking-[0.22em] uppercase font-medium">
                      <span className="inline-flex items-center gap-2">
                        <span aria-hidden="true" className="inline-block h-px w-6" style={{
                        background: "hsl(215 90% 45%)"
                      }} />
                        Menu
                      </span>
                    </SheetTitle>
                  </SheetHeader>

                  <MenuProvider menu_id="16">
                    <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
                  </MenuProvider>

                  <div className="mt-auto p-6">
                    <Link to="/ContactsPage" onClick={() => setMobileOpen(false)} className="inline-flex items-center gap-3 bg-primary text-primary-foreground rounded-lg pl-6 pr-2 py-2 hover:bg-primary/90 transition-colors duration-380 w-full justify-between">
                      <span className="text-[14px] font-medium tracking-[0.01em]">
                        Consulte-nos
                      </span>
                      <span className="flex items-center justify-center rounded-lg" style={{
                      width: 28,
                      height: 28,
                      background: "hsl(210 20% 98%)"
                    }}>
                        <ArrowUpRight className="w-3.5 h-3.5" style={{
                        color: "hsl(215 90% 45%)"
                      }} />
                      </span>
                    </Link>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </section>;
}